import Phaser from 'phaser';
import type { SignalsPlugin } from '@repo/plugin-signals';
import type { TimeTrackerPlugin } from '@repo/plugin-timetracker';
import { Button, PauseModal, defaultTheme } from '@repo/ui';
import {
  type GameState,
  PRESETS,
  BLACK,
  WHITE,
  EMPTY,
  generateGame,
  cycleCell,
  fillCell,
  clearCell,
  requestHint,
  moveSelection,
  setSelection,
  isImmutable,
  hasError,
  getRowCounts,
  getColumnCounts,
  getCellValue,
} from '../game';

/**
 * Type for scene with plugins
 */
interface SceneWithPlugins extends Phaser.Scene {
  signals: SignalsPlugin;
  timetracker: TimeTrackerPlugin;
}

// Colors
const COLORS = {
  background: 0x1a1a2e,
  gridBackground: 0x16213e,
  cellBackground: 0x2a3a5a, // Neutral cell background
  cellSelected: 0x533483,
  cellImmutable: 0x1f3858, // Slightly different for given cells
  cellError: 0x8b0000,
  cellComplete: 0x228b22,
  // Colors for filled cells
  cellBlack: 0x1a1a1a, // Black fill
  cellWhite: 0xf0f0f0, // White fill
  cellBlackGiven: 0x2a2a2a, // Slightly lighter black for given
  cellWhiteGiven: 0xffffff, // Pure white for given
  // Error overlay
  errorTint: 0xff4444,
  countNormal: '#888888',
  countComplete: '#44ff44',
  countError: '#ff4444',
  gridLine: 0x404040,
  gridLineLight: 0x505050,
};

/**
 * Main game scene for Tic-Tac-Logic
 */
export class GameScene extends Phaser.Scene {
  private gameState!: GameState;
  private signals!: SignalsPlugin;
  private timetracker!: TimeTrackerPlugin;

  // Visual elements
  private gridContainer!: Phaser.GameObjects.Container;
  private cellGraphics: Phaser.GameObjects.Graphics[][] = [];
  private cellTexts: Phaser.GameObjects.Text[][] = [];
  private selectionRect!: Phaser.GameObjects.Graphics;

  // Row/column count displays
  private rowCountTexts: Phaser.GameObjects.Text[] = [];
  private colCountTexts: Phaser.GameObjects.Text[] = [];

  // Layout
  private cellSize = 50;
  private gridX = 0;
  private gridY = 0;

  // Game settings
  private currentPresetIndex = 0;

  // UI elements
  private statusText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private pauseModal!: PauseModal;
  private timerUpdateEvent?: Phaser.Time.TimerEvent;

  constructor() {
    super({ key: 'GameScene' });
  }

  create(): void {
    const { width } = this.cameras.main;

    // Initialize plugins
    this.signals = (this as unknown as SceneWithPlugins).signals;
    this.timetracker = (this as unknown as SceneWithPlugins).timetracker;

    // Background
    this.cameras.main.setBackgroundColor(COLORS.background);

    // Setup pause/resume signal listeners
    this.setupPauseListeners();

    // Title
    const title = this.add.text(width / 2, 30, 'LIKEVEKT', {
      fontSize: '36px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);

    // Subtitle
    const subtitle = this.add.text(width / 2, 60, 'Fyll rutenettet med svart og hvitt', {
      fontSize: '16px',
      color: '#888888',
    });
    subtitle.setOrigin(0.5);

    // Create UI buttons
    this.createUIButtons();

    // Generate initial puzzle
    this.startNewGame();

    // Setup keyboard input
    this.setupKeyboardInput();

    // Start timer display
    this.startTimerDisplay();
  }

  /**
   * Create UI buttons
   */
  private createUIButtons(): void {
    const { width, height } = this.cameras.main;

    // New Game button
    new Button(this, {
      x: width - 100,
      y: 50,
      width: 150,
      height: 40,
      text: 'New Game',
      fontSize: 16,
      theme: defaultTheme,
      variant: 'primary',
      onClick: () => this.startNewGame(),
    });

    // Hint button
    new Button(this, {
      x: width - 100,
      y: 100,
      width: 150,
      height: 40,
      text: 'Hint',
      fontSize: 16,
      theme: defaultTheme,
      variant: 'secondary',
      onClick: () => this.requestHintAction(),
    });

    // Pause button
    new Button(this, {
      x: width - 100,
      y: 150,
      width: 150,
      height: 40,
      text: 'Pause',
      fontSize: 16,
      theme: defaultTheme,
      variant: 'secondary',
      onClick: () => this.pauseGame(),
    });

    // Size/difficulty selector buttons
    const presetY = height - 60;
    const presetWidth = 100;
    const startX = 80;

    // Show first 4 presets
    for (let i = 0; i < Math.min(4, PRESETS.length); i++) {
      const preset = PRESETS[i]!;
      new Button(this, {
        x: startX + i * (presetWidth + 10),
        y: presetY,
        width: presetWidth,
        height: 35,
        text: preset.name,
        fontSize: 11,
        theme: defaultTheme,
        variant: i === this.currentPresetIndex ? 'success' : 'secondary',
        onClick: () => this.selectPreset(i),
      });
    }

    // Show next 4 presets on second row
    for (let i = 4; i < Math.min(8, PRESETS.length); i++) {
      const preset = PRESETS[i]!;
      new Button(this, {
        x: startX + (i - 4) * (presetWidth + 10),
        y: presetY + 40,
        width: presetWidth,
        height: 35,
        text: preset.name,
        fontSize: 11,
        theme: defaultTheme,
        variant: i === this.currentPresetIndex ? 'success' : 'secondary',
        onClick: () => this.selectPreset(i),
      });
    }

    // Status text
    this.statusText = this.add.text(width / 2, height - 130, 'Click a cell to cycle: empty → ● → ○', {
      fontSize: '14px',
      color: '#888888',
    });
    this.statusText.setOrigin(0.5);

    // Timer text
    this.timerText = this.add.text(20, 20, 'Time: 0:00', {
      fontSize: '16px',
      color: '#ffffff',
    });
  }

  /**
   * Start a new game
   */
  private startNewGame(): void {
    const preset = PRESETS[this.currentPresetIndex]!;

    // Generate puzzle
    this.gameState = generateGame(preset.size, preset.difficulty);

    // Calculate layout
    this.calculateLayout();

    // Create grid visuals
    this.createGrid();

    // Emit game start signal
    if (this.signals) {
      this.signals.global.lifecycle.gameStart.emit({
        timestamp: Date.now(),
        sceneKey: this.scene.key,
      });
    }

    this.updateStatus(`New ${preset.name} puzzle generated!`);
  }

  /**
   * Calculate grid layout
   */
  private calculateLayout(): void {
    const { width, height } = this.cameras.main;
    const { size } = this.gameState;

    // Calculate cell size to fit the screen
    const maxGridWidth = width - 280; // Leave space for buttons and counters
    const maxGridHeight = height - 250; // Leave space for title, footer, counters

    this.cellSize = Math.min(
      Math.floor(maxGridWidth / (size + 1)), // +1 for count column
      Math.floor(maxGridHeight / (size + 1)), // +1 for count row
      60
    );

    const gridWidth = size * this.cellSize;
    const gridHeight = size * this.cellSize;

    // Center grid, accounting for count displays
    this.gridX = (width - 200 - gridWidth) / 2 + 30;
    this.gridY = (height - gridHeight) / 2 + 20;
  }

  /**
   * Create grid visual elements
   */
  private createGrid(): void {
    // Clear existing grid
    if (this.gridContainer) {
      this.gridContainer.destroy();
    }

    this.gridContainer = this.add.container(this.gridX, this.gridY);
    this.cellGraphics = [];
    this.cellTexts = [];
    this.rowCountTexts = [];
    this.colCountTexts = [];

    const { size } = this.gameState;

    // Create cells
    for (let y = 0; y < size; y++) {
      this.cellGraphics[y] = [];
      this.cellTexts[y] = [];

      for (let x = 0; x < size; x++) {
        const cellX = x * this.cellSize;
        const cellY = y * this.cellSize;

        // Cell background
        const graphics = this.add.graphics();
        this.cellGraphics[y]![x] = graphics;
        this.gridContainer.add(graphics);

        // Cell text (X or O)
        const text = this.add.text(
          cellX + this.cellSize / 2,
          cellY + this.cellSize / 2,
          '',
          {
            fontSize: `${this.cellSize * 0.6}px`,
            fontStyle: 'bold',
          }
        );
        text.setOrigin(0.5);
        this.cellTexts[y]![x] = text;
        this.gridContainer.add(text);

        // Make cell interactive
        const zone = this.add.zone(
          cellX + this.cellSize / 2,
          cellY + this.cellSize / 2,
          this.cellSize,
          this.cellSize
        );
        zone.setInteractive({ useHandCursor: true });
        zone.on('pointerdown', () => this.onCellClick(x, y));
        this.gridContainer.add(zone);
      }
    }

    // Create row count displays (on the right)
    for (let y = 0; y < size; y++) {
      const countX = size * this.cellSize + 15;
      const countY = y * this.cellSize + this.cellSize / 2;

      const countText = this.add.text(countX, countY, '', {
        fontSize: `${this.cellSize * 0.3}px`,
        color: COLORS.countNormal,
      });
      countText.setOrigin(0, 0.5);
      this.rowCountTexts[y] = countText;
      this.gridContainer.add(countText);
    }

    // Create column count displays (at the bottom)
    for (let x = 0; x < size; x++) {
      const countX = x * this.cellSize + this.cellSize / 2;
      const countY = size * this.cellSize + 10;

      const countText = this.add.text(countX, countY, '', {
        fontSize: `${this.cellSize * 0.3}px`,
        color: COLORS.countNormal,
      });
      countText.setOrigin(0.5, 0);
      this.colCountTexts[x] = countText;
      this.gridContainer.add(countText);
    }

    // Create selection rectangle
    this.selectionRect = this.add.graphics();
    this.gridContainer.add(this.selectionRect);

    // Initial render
    this.renderGrid();
  }

  /**
   * Render the grid based on current state
   */
  private renderGrid(): void {
    const { size } = this.gameState;
    const target = size / 2;
    const padding = 3;
    const innerSize = this.cellSize - padding * 2;
    const radius = innerSize * 0.42; // Circle radius

    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const cellX = x * this.cellSize;
        const cellY = y * this.cellSize;
        const centerX = cellX + this.cellSize / 2;
        const centerY = cellY + this.cellSize / 2;
        const value = getCellValue(this.gameState, x, y);
        const immutable = isImmutable(this.gameState, x, y);
        const error = hasError(this.gameState, x, y);
        const selected = x === this.gameState.selectedX && y === this.gameState.selectedY;

        // Draw cell background
        const graphics = this.cellGraphics[y]?.[x];
        if (!graphics) continue;
        graphics.clear();

        // Cell background - neutral gray
        let bgColor = COLORS.cellBackground;
        if (this.gameState.completed) {
          bgColor = COLORS.cellComplete;
        } else if (selected) {
          bgColor = COLORS.cellSelected;
        } else if (immutable) {
          bgColor = COLORS.cellImmutable;
        }

        graphics.fillStyle(bgColor, 1);
        graphics.fillRoundedRect(cellX + 2, cellY + 2, this.cellSize - 4, this.cellSize - 4, 6);
        graphics.lineStyle(2, COLORS.gridLine, 1);
        graphics.strokeRoundedRect(cellX + 2, cellY + 2, this.cellSize - 4, this.cellSize - 4, 6);

        // Draw filled circle for BLACK or WHITE
        if (value === BLACK) {
          const fillColor = immutable ? COLORS.cellBlackGiven : COLORS.cellBlack;
          graphics.fillStyle(fillColor, 1);
          graphics.fillCircle(centerX, centerY, radius);
          // Add subtle border for depth
          graphics.lineStyle(2, 0x000000, 0.5);
          graphics.strokeCircle(centerX, centerY, radius);
        } else if (value === WHITE) {
          const fillColor = immutable ? COLORS.cellWhiteGiven : COLORS.cellWhite;
          graphics.fillStyle(fillColor, 1);
          graphics.fillCircle(centerX, centerY, radius);
          // Add dark border for visibility
          graphics.lineStyle(2, 0x333333, 1);
          graphics.strokeCircle(centerX, centerY, radius);
        }

        // Draw error indicator (red ring around the circle)
        if (error && value !== EMPTY) {
          graphics.lineStyle(3, COLORS.errorTint, 1);
          graphics.strokeCircle(centerX, centerY, radius + 3);
        }

        // Hide text elements (not used anymore)
        const text = this.cellTexts[y]?.[x];
        if (text) {
          text.setVisible(false);
        }
      }
    }

    // Update row counts
    for (let y = 0; y < size; y++) {
      const counts = getRowCounts(this.gameState, y);
      const countText = this.rowCountTexts[y];
      if (countText) {
        countText.setText(`●${counts.x} ○${counts.o}`);

        let color = COLORS.countNormal;
        if (counts.x > target || counts.o > target) {
          color = COLORS.countError;
        } else if (counts.x === target && counts.o === target) {
          color = COLORS.countComplete;
        }
        countText.setColor(color);
      }
    }

    // Update column counts
    for (let x = 0; x < size; x++) {
      const counts = getColumnCounts(this.gameState, x);
      const countText = this.colCountTexts[x];
      if (countText) {
        countText.setText(`●${counts.x}\n○${counts.o}`);

        let color = COLORS.countNormal;
        if (counts.x > target || counts.o > target) {
          color = COLORS.countError;
        } else if (counts.x === target && counts.o === target) {
          color = COLORS.countComplete;
        }
        countText.setColor(color);
      }
    }

    // Update selection rectangle
    this.selectionRect.clear();
    if (!this.gameState.completed) {
      const selX = this.gameState.selectedX * this.cellSize;
      const selY = this.gameState.selectedY * this.cellSize;

      this.selectionRect.lineStyle(3, 0xffff00, 1);
      this.selectionRect.strokeRoundedRect(selX, selY, this.cellSize, this.cellSize, 8);
    }
  }

  /**
   * Handle cell click
   */
  private onCellClick(x: number, y: number): void {
    if (this.gameState.completed) return;

    // Set selection and cycle cell
    setSelection(this.gameState, x, y);
    cycleCell(this.gameState, x, y);
    this.renderGrid();
    this.checkWin();
  }

  /**
   * Setup keyboard input
   */
  private setupKeyboardInput(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.gameState.completed) return;

      const key = event.key.toLowerCase();

      // Arrow keys - move selection
      if (key === 'arrowup') {
        moveSelection(this.gameState, 0, -1);
        this.renderGrid();
        return;
      }
      if (key === 'arrowdown') {
        moveSelection(this.gameState, 0, 1);
        this.renderGrid();
        return;
      }
      if (key === 'arrowleft') {
        moveSelection(this.gameState, -1, 0);
        this.renderGrid();
        return;
      }
      if (key === 'arrowright') {
        moveSelection(this.gameState, 1, 0);
        this.renderGrid();
        return;
      }

      // B or 1 - place BLACK
      if (key === 'b' || key === '1') {
        const { selectedX, selectedY } = this.gameState;
        fillCell(this.gameState, selectedX, selectedY, BLACK);
        this.renderGrid();
        this.checkWin();
        return;
      }

      // W or 2 - place WHITE
      if (key === 'w' || key === '2') {
        const { selectedX, selectedY } = this.gameState;
        fillCell(this.gameState, selectedX, selectedY, WHITE);
        this.renderGrid();
        this.checkWin();
        return;
      }

      // Space - cycle
      if (key === ' ') {
        const { selectedX, selectedY } = this.gameState;
        cycleCell(this.gameState, selectedX, selectedY);
        this.renderGrid();
        this.checkWin();
        return;
      }

      // Delete/Backspace/0 - clear cell
      if (key === 'delete' || key === 'backspace' || key === '0') {
        const { selectedX, selectedY } = this.gameState;
        clearCell(this.gameState, selectedX, selectedY);
        this.renderGrid();
        return;
      }

      // H - hint
      if (key === 'h') {
        this.requestHintAction();
        return;
      }
    });
  }

  /**
   * Setup pause modal
   */
  private setupPauseListeners(): void {
    this.pauseModal = new PauseModal(this, {
      theme: defaultTheme,
      title: 'PAUSED',
    });
  }

  /**
   * Pause the game
   */
  private pauseGame(): void {
    if (this.gameState.completed) return;
    this.timetracker?.pause();
  }

  /**
   * Request a hint
   */
  private requestHintAction(): void {
    const result = requestHint(this.gameState);
    if (result) {
      setSelection(this.gameState, result.x, result.y);
      this.renderGrid();
      this.updateStatus(`Hint: ${result.value === BLACK ? '●' : '○'} placed`);
      this.checkWin();
    } else {
      this.updateStatus('No hints available');
    }
  }

  /**
   * Select a preset
   */
  private selectPreset(index: number): void {
    this.currentPresetIndex = index;
    this.startNewGame();
  }

  /**
   * Check for win condition
   */
  private checkWin(): void {
    if (this.gameState.completed) {
      this.stopTimerDisplay();

      const cheated = this.gameState.cheated;
      const time = this.timetracker?.getFormattedTime() ?? '0:00';
      const elapsedSeconds = this.timetracker?.getElapsedSeconds() ?? 0;

      this.updateStatus(
        cheated
          ? `Puzzle complete (with hints) - Time: ${time}`
          : `Congratulations! Puzzle solved - Time: ${time}`
      );

      // Emit game over signal
      if (this.signals) {
        this.signals.global.lifecycle.gameOver.emit({
          timestamp: Date.now(),
          sceneKey: this.scene.key,
          finalScore: elapsedSeconds,
          reason: 'puzzle_completed',
        });
      }
    }
  }

  /**
   * Update status text
   */
  private updateStatus(message: string): void {
    this.statusText.setText(message);
  }

  /**
   * Start timer display update loop
   */
  private startTimerDisplay(): void {
    this.stopTimerDisplay();
    this.updateTimerDisplay();

    this.timerUpdateEvent = this.time.addEvent({
      delay: 1000,
      callback: () => this.updateTimerDisplay(),
      loop: true,
    });
  }

  /**
   * Stop timer display updates
   */
  private stopTimerDisplay(): void {
    if (this.timerUpdateEvent) {
      this.timerUpdateEvent.remove();
      this.timerUpdateEvent = undefined;
    }
  }

  /**
   * Update the timer display text
   */
  private updateTimerDisplay(): void {
    const formattedTime = this.timetracker?.getFormattedTime() ?? '00:00';
    this.timerText.setText(`Time: ${formattedTime}`);
  }
}
